import { Timezones, DateType } from '@hero/schema';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { CONFIG } from '../config';

dayjs.extend(weekday);
dayjs.extend(utc);
dayjs.extend(tz);

const DEFAULT_TIMEZONE = CONFIG.time.defaultTimezone;

type DateTimeFrame = Extract<
  dayjs.ManipulateType,
  'second' | 'minute' | 'hour' | 'day' | 'month' | 'year' | 'week'
>;

type AddToDate = (
  /**
   * 1 d
   * 2 m
   * 3 weeks
   * etc
   */
  value: number,
  type: DateTimeFrame,
  /**
   * the date you want to add to. If empty, it we will use the current date, and adds to it
   */
  date?: Date,
) => FormattedDate;

type FormattedDate = {
  /**
   * Dayjs class which contain all functions for a given date
   */
  dayjs: dayjs.Dayjs;
  /**
   * the number of milliseconds since the Unix Epoch.
   */
  timestamp: number;
  /**
   * native Date object
   */
  date: Date;
};

/**
 * this function helps in consistency with formatting dates.
 * @param dayjs a Dayjs Class
 */
const getDateInfo = (dayjs: dayjs.Dayjs): FormattedDate => {
  return {
    dayjs,
    timestamp: dayjs.valueOf(),
    date: dayjs.toDate(),
  };
};
/**
 *
 * @param value choose the value of increase such 1, 2, 3, etc
 * @param type choose the type of increase you want such as day/month. Based on the value of increase, it will be 1day/1month/etc
 * @param date the date you want to increase by. If it's empty, then we will use the current time
 * @returns
 */
export const addToDate: AddToDate = (value, type, date) => {
  const newDate = dayjs(date).add(value, type);
  return getDateInfo(newDate);
};

export const getDateFormatted = (date: dayjs.ConfigType, format = 'D/M/YYYY') => {
  const updatedDate = dayjs(date).format(format);
  return updatedDate;
};
/** this is used for cloudinary and images to be saved in the same date format for analysis. */
export const getDateFormattedForImages = (date: dayjs.ConfigType) => {
  return getDateFormatted(date, 'DD/MM/YYYY');
};
export const getDateFormattedHour = (date: dayjs.ConfigType) => {
  const formattedDate = getDateFormatted(date, 'hh:mm A');
  return formattedDate;
};

export const getTimestampNow = () => {
  const timestamp = dayjs().valueOf();
  return timestamp;
};

export const getDateNow = () => {
  return dayjs().toDate();
};

export const getDateNowInTimezone = (timezone: Timezones = DEFAULT_TIMEZONE) => {
  const now = getDateNow();
  return getDateInTimezone(now, timezone).toDate();
};
export const getDate = (date?: dayjs.ConfigType) => {
  return dayjs(date);
};
/**
 * convert a date into a specific timezone
 *
 * @param date the date you want to convert timezone in ... if empty it will get the current time
 * @param timezone the target timezone ... by default it's Saudi Time (Asia/Riyadh)
 */
export const getDateInTimezone = (
  date: dayjs.ConfigType,
  timezone: Timezones = DEFAULT_TIMEZONE,
) => {
  const dateInTimezone = dayjs.utc(date).tz(timezone);
  return dateInTimezone;
};

export const getFirstDayOfNextMonth = (date = getDateNow()) => {
  const updatedDate = dayjs(date).endOf('month').add(1, 'day');
  return updatedDate;
};
export const getWeekDays = (anyDate?: dayjs.ConfigType) => {
  const date = dayjs(anyDate).startOf('day');
  return {
    sunday: date.weekday(0).format('MMM, DD dd, YYYY'),
    monday: date.weekday(1).format('MMM, DD dd, YYYY'),
    tuesday: date.weekday(2).format('MMM, DD dd, YYYY'),
    wednesday: date.weekday(3).format('MMM, DD dd, YYYY'),
    thursday: date.weekday(4).format('MMM, DD dd, YYYY'),
    friday: date.weekday(5).format('MMM, DD dd, YYYY'),
    saturday: date.weekday(6).format('MMM, DD dd, YYYY'),
  };
};
export const nextWorkingDays = (date?: dayjs.ConfigType) => {
  return dayjs().weekday(4);
};

/**
 * this func adds X number of periods to a date.
 *
 * @param count number of weeks to add to the date
 * @param period the type of period you want to count on such as days, weeks.
 * @param targetDate the target date you want to add to
 */
export const addToDateV2 = (
  count: number,
  period: DateTimeFrame,
  targetDate = getDateNowInTimezone(DEFAULT_TIMEZONE),
  timeZone: Timezones = DEFAULT_TIMEZONE,
) => {
  const exactDate = getDateInTimezone(targetDate, timeZone).add(count, period);
  const startOfPeriod =
    period === 'week' ? exactDate.weekday(0).startOf('day') : exactDate.startOf(period);

  // weekday(6) means Saturday
  const endOfPeriod =
    period === 'week' ? exactDate.weekday(6).endOf('day') : exactDate.endOf(period);

  const thursday = exactDate.weekday(4).startOf('day');

  return {
    /** the exact date after adding the count & period. For example,
     *
     * if `count = 1` & `period = week` & `targetDate = Feb 5`:
     *
     * then the exact date will be `Feb 12 Wednesday` because we added 1 week to the target date
     * */
    exactDate,
    /** the beginning date of the `period`. For example, if `exactDate` is `Feb 12, Wednesday`,
     *
     * then `startOfPeriod` will be `Feb 9, Sunday`  because it's the first day of the week.
     * */
    startOfPeriod,

    /** the thursday date of the `period`. For example, if `exactDate` is `Feb 12, Wednesday`,
     *
     * then the `thursday` will be `Feb 13, Thursday`  because it's the thursday of the week.
     * */

    thursday,
    /** the end date of the `period`. For example, if `exactDate` is `Feb 12, Wednesday`,
     *
     * then the `endOfPeriod` will be `Feb 13, Thursday`  because it's the last day of the week.
     * */
    endOfPeriod,
  };
};

/**
 * this function compares a given date with the current time and returns if it has passed or not
 * @param date the comparison date
 * @param type the type diff you want to return min, hours, days diff, etc
 * @returns diff value, and if time has passed or not
 */

export const getDateDiffNow = (date: Date, type: DateTimeFrame = 'second') => {
  const now = dayjs();

  if (type === 'day') {
    // Compare calendar dates (start-of-day) so that same-day times always
    // produce diff === 0 regardless of the exact hours/minutes.
    // Without this, a due date of "today at 7 PM" checked at "today 8 PM"
    // could appear as "1 day late" due to raw timestamp arithmetic.
    const nowDay = now.startOf('day');
    const dateDay = dayjs(date).startOf('day');
    const diff = nowDay.diff(dateDay, 'day');
    return { diff, dateHasPassed: diff > 0 };
  }

  const diff = now.diff(date, type);
  const dateHasPassed = diff >= 0;
  return {
    diff,
    dateHasPassed,
  };
};

export const getDateDiff = (date1: Date, date2: Date, type: DateTimeFrame = 'minute') => {
  const fromDate = dayjs(date1);
  const result = fromDate.diff(date2, type);
  if (type === 'day') {
    // Use calendar-day comparison (start-of-day) for accurate business-day counting.
    // This ensures that two timestamps on the same calendar day always produce 0,
    // and timestamps on adjacent calendar days always produce 1 (or -1).
    // Previous Math.ceil approach would round a 3-minute same-day gap to 1 day.
    return fromDate.startOf('day').diff(dayjs(date2).startOf('day'), 'day');
  }
  return Math.round(result);
};

/**
 * Get date range (from and to) based on a date type and timezone
 *
 * @param dateType The type of date range (today, yesterday, this_week, etc.)
 * @param timezone The timezone to use for calculations (default: 'Asia/Riyadh')
 * @returns An object with `from` and `to` Date objects, or undefined if dateType is invalid
 */
export const getDateRangeByType = (
  dateType: DateType,
  timezone: Timezones = DEFAULT_TIMEZONE,
): { from: Date; to: Date } | undefined => {
  const quarters = [
    [0, 1, 2], // January, February, March
    [3, 4, 5], // April, May, June
    [6, 7, 8], // July, August, September
    [9, 10, 11], // October, November, December
  ];
  const now = getDateInTimezone(getDateNow(), timezone);
  let from: Date;
  let to: Date;

  switch (dateType) {
    case 'this_week':
      from = now.startOf('week').toDate();
      to = now.endOf('week').toDate();
      break;

    case 'last_week':
      from = now.subtract(1, 'week').startOf('week').toDate();
      to = now.subtract(1, 'week').endOf('week').toDate();
      break;

    case 'next_week':
      from = now.add(1, 'week').startOf('week').toDate();
      to = now.add(1, 'week').endOf('week').toDate();
      break;

    case 'this_month':
      from = now.startOf('month').toDate();
      to = now.endOf('month').toDate();
      break;

    case 'last_month':
      from = now.subtract(1, 'month').startOf('month').toDate();
      to = now.subtract(1, 'month').endOf('month').toDate();
      break;

    case 'next_month':
      from = now.add(1, 'month').startOf('month').toDate();
      to = now.add(1, 'month').endOf('month').toDate();
      break;

    case 'this_quarter': {
      const currentMonth = now.month();
      const currentQuarterIndex = quarters.findIndex(quarter => quarter.includes(currentMonth));
      const currentQuarter = quarters[currentQuarterIndex];
      from = now.month(currentQuarter[0]).startOf('month').toDate();
      to = now
        .month(currentQuarter[currentQuarter.length - 1])
        .endOf('month')
        .toDate();
      break;
    }

    case 'last_quarter': {
      const currentMonth = now.month();
      const currentQuarterIndex = quarters.findIndex(quarter => quarter.includes(currentMonth));
      const lastQuarterIndex =
        currentQuarterIndex === 0 ? quarters.length - 1 : currentQuarterIndex - 1;
      const lastQuarter = quarters[lastQuarterIndex];
      from = now.month(lastQuarter[0]).startOf('month').toDate();
      to = now
        .month(lastQuarter[lastQuarter.length - 1])
        .endOf('month')
        .toDate();
      break;
    }

    case 'next_quarter': {
      const currentMonth = now.month();
      const currentQuarterIndex = quarters.findIndex(quarter => quarter.includes(currentMonth));
      const nextQuarterIndex =
        currentQuarterIndex === quarters.length - 1 ? 0 : currentQuarterIndex + 1;
      const nextQuarter = quarters[nextQuarterIndex];
      from = now.month(nextQuarter[0]).startOf('month').toDate();
      to = now
        .month(nextQuarter[nextQuarter.length - 1])
        .endOf('month')
        .toDate();
      break;
    }

    case 'this_year':
      from = now.startOf('year').toDate();
      to = now.endOf('year').toDate();
      break;

    case 'last_year':
      from = now.subtract(1, 'year').startOf('year').toDate();
      to = now.subtract(1, 'year').endOf('year').toDate();
      break;

    case 'next_year':
      from = now.add(1, 'year').startOf('year').toDate();
      to = now.add(1, 'year').endOf('year').toDate();
      break;

    case 'last_30_days':
      from = now.subtract(30, 'day').startOf('day').toDate();
      to = now.endOf('day').toDate();
      break;

    case 'next_30_days':
      from = now.startOf('day').toDate();
      to = now.add(30, 'day').endOf('day').toDate();
      break;

    case 'last_60_days':
      from = now.subtract(60, 'day').startOf('day').toDate();
      to = now.endOf('day').toDate();
      break;

    case 'next_60_days':
      from = now.startOf('day').toDate();
      to = now.add(60, 'day').endOf('day').toDate();
      break;

    case 'last_90_days':
      from = now.subtract(90, 'day').startOf('day').toDate();
      to = now.endOf('day').toDate();
      break;

    case 'next_90_days':
      from = now.startOf('day').toDate();
      to = now.add(90, 'day').endOf('day').toDate();
      break;
    case 'yesterday':
      from = now.subtract(1, 'day').startOf('day').toDate();
      to = now.subtract(1, 'day').endOf('day').toDate();
      break;

    case 'tomorrow':
      from = now.add(1, 'day').startOf('day').toDate();
      to = now.add(1, 'day').endOf('day').toDate();
      break;
    default:
      // today
      from = now.startOf('day').toDate();
      to = now.endOf('day').toDate();
      break;
  }

  return { from, to };
};

/**
 * Date format types supported by formatDateMain
 */
export type DateFormatType =
  | 'relative'
  | 'relative_without_time'
  | 'date'
  | 'date_time'
  | 'date_day_time'
  | 'numeric'
  | 'numeric_time'
  | 'time'
  | 'day_date_time'
  | 'date_day'
  | 'day_date_time_full'
  | 'web_format';

/**
 * Comprehensive date formatting function that supports multiple format types.
 * Accepts a single format type or an array of format types (returns first valid format).
 *
 * @param options - Formatting options
 * @param options.date - Date to format (Date, string, undefined, or null)
 * @param options.format - Single format type or array of format types
 * @param options.timezone - Optional timezone to convert the date to (default: uses date's original timezone)
 * @param options.ifDateisNotRelative - Optional format to use when 'relative' format is selected but date is not today or tomorrow
 * @returns Formatted date string, or empty string if date is invalid/undefined
 *
 * @example
 * formatDateMain({ date: new Date(), format: 'relative' }) // "Today 10:00 AM"
 * formatDateMain({ date: new Date(), format: ['relative', 'date_time'] }) // Uses first valid format
 * formatDateMain({ date: new Date(), format: 'date_time', timezone: 'Asia/Riyadh' }) // Converts to Riyadh timezone
 * formatDateMain({ date: futureDate, format: 'relative', ifDateisNotRelative: 'numeric_time' }) // Uses numeric_time if not today/tomorrow
 */
export const formatDateMain = (options: {
  date: Date | string | undefined | null;
  format: DateFormatType | DateFormatType[];
  timezone?: Timezones;
  ifDateisNotRelative?: DateFormatType | DateFormatType[];
}): string => {
  const { date, format, timezone, ifDateisNotRelative } = options;

  // Handle undefined/null dates
  if (!date) {
    return '';
  }

  // Convert to timezone if provided, otherwise use date as-is
  const d = timezone ? getDateInTimezone(date, timezone) : dayjs(date);
  if (!d.isValid()) {
    return '';
  }

  // For relative comparisons, also convert 'now' to the same timezone if provided
  const now = timezone ? getDateInTimezone(getDateNow(), timezone) : dayjs();
  const formats = Array.isArray(format) ? format : [format];

  /**
   * Helper function to format relative dates (with or without time)
   * @param includeTime - Whether to include time in the formatted string
   * @returns Formatted relative date string
   */
  const formatRelativeDate = (includeTime: boolean): string => {
    const timeStr = includeTime ? d.format('h:mm A') : '';

    // Check if same day
    if (d.isSame(now, 'day')) {
      return includeTime ? `Today ${timeStr}` : 'Today';
    }

    // Check if tomorrow
    if (d.isSame(now.add(1, 'day'), 'day')) {
      return includeTime ? `Tomorrow ${timeStr}` : 'Tomorrow';
    }

    // If date is not today or tomorrow, use ifDateisNotRelative format if provided
    if (ifDateisNotRelative) {
      // Recursively call formatDateMain with the ifDateisNotRelative format
      const fallbackFormats = Array.isArray(ifDateisNotRelative)
        ? ifDateisNotRelative
        : [ifDateisNotRelative];

      // Process the fallback formats
      for (const fallbackFmt of fallbackFormats) {
        const fallbackResult = formatDateMain({
          date,
          format: fallbackFmt,
          timezone,
        });
        if (fallbackResult) {
          return fallbackResult;
        }
      }
    }

    // Fallback to original logic if ifDateisNotRelative is not provided or didn't produce a result
    // Check if yesterday
    if (d.isSame(now.subtract(1, 'day'), 'day')) {
      return includeTime ? `Yesterday ${timeStr}` : 'Yesterday';
    }

    // Check if within the current week (same week)
    if (d.isSame(now, 'week')) {
      return includeTime ? `${d.format('dddd')} ${timeStr}` : d.format('dddd');
    }

    // Beyond current week - show day name, date, and optionally time with "at" separator
    const dayName = d.format('dddd');
    const isDifferentYear = !d.isSame(now, 'year');

    if (isDifferentYear) {
      const dateStr = d.format('MMM D, YYYY');
      return includeTime ? `${dayName}, ${dateStr} at ${timeStr}` : `${dayName}, ${dateStr}`;
    } else {
      const dateStr = d.format('MMM D');
      return includeTime ? `${dayName}, ${dateStr} at ${timeStr}` : `${dayName}, ${dateStr}`;
    }
  };

  // Try each format in order, return first valid result
  for (const fmt of formats) {
    let result = '';

    switch (fmt) {
      case 'relative': {
        result = formatRelativeDate(true);
        break;
      }

      case 'relative_without_time': {
        result = formatRelativeDate(false);
        break;
      }

      case 'date': {
        const includeYear = !d.isSame(now, 'year');
        result = includeYear ? d.format('MMM D, YYYY') : d.format('MMM D');
        break;
      }

      case 'date_time': {
        const includeYear = !d.isSame(now, 'year');
        result = includeYear ? d.format('MMM D, hh:mm A, YYYY') : d.format('MMM D, hh:mm A');
        break;
      }

      case 'date_day_time': {
        const includeYear = !d.isSame(now, 'year');
        result = includeYear
          ? d.format('MMM D, ddd hh:mm A, YYYY')
          : d.format('MMM D, ddd hh:mm A');
        break;
      }

      case 'numeric': {
        result = d.format('D/M/YYYY');
        break;
      }

      case 'numeric_time': {
        result = d.format('D/M/YYYY h:mm A');
        break;
      }

      case 'time': {
        result = d.format('hh:mm A');
        break;
      }

      case 'day_date_time': {
        result = d.format('ddd h:mm A (MMM D)');
        break;
      }

      case 'date_day': {
        const includeYear = !d.isSame(now, 'year');
        result = includeYear ? d.format('MMM D, ddd, YYYY') : d.format('MMM D, ddd');
        break;
      }

      case 'day_date_time_full': {
        const includeYear = !d.isSame(now, 'year');
        result = includeYear
          ? d.format('ddd, MMM D, hh:mm A, YYYY')
          : d.format('ddd, MMM D, hh:mm A');
        break;
      }

      case 'web_format': {
        // Equivalent to date-fns "EEE dd MMM, hh:mm a" format
        result = d.format('ddd DD MMM, hh:mm A');
        break;
      }

      default:
        continue; // Skip unknown format types
    }

    if (result) {
      return result;
    }
  }

  return '';
};

/**
 * Checks if two dates fall on the same calendar day.
 *
 * Both dates are converted to the default timezone (Asia/Riyadh) before comparing
 * to avoid incorrect results caused by the runtime's local timezone (e.g., a UTC server
 * would see 11:30 PM Riyadh as the next day without this conversion).
 */
export const isSameDay = (date1: Date, date2: Date) => {
  return getDateInTimezone(date1).isSame(getDateInTimezone(date2), 'day');
};

/**
 * Format a `{ from, to }` date pair as a single-line period label,
 * reusing `formatDateMain({ format: 'date' })` for each end so the
 * "MMM D" / "MMM D, YYYY" rule stays canonical (year omitted when the
 * date falls in the current year — exact same logic as every other
 * date in the product).
 *
 * Examples:
 *   - Current year on both ends: "Jun 10 → Dec 10"
 *   - Cross-year range (start = current year, end = next year):
 *     "Jun 14 → Jun 14, 2027"
 *
 * Returns an empty string when either date is missing or invalid so
 * call sites can `{label && <…>}` cleanly.
 */
export const formatDatePeriod = (
  period:
    | { from?: Date | string | null; to?: Date | string | null }
    | null
    | undefined,
): string => {
  if (!period) return '';
  const from = formatDateMain({ date: period.from, format: 'date' });
  const to = formatDateMain({ date: period.to, format: 'date' });
  if (!from || !to) return '';
  return `${from} → ${to}`;
};
